import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { TweenMax } from 'gsap';
import { useDispatch, useSelector } from "react-redux";
import { WorkspaceContainer, SmartWorkspaceWrapper } from "../../../workspace/workspace-components";
import { CenterCircle, MaskArea, Overlay, OverlayContainer, SfWorkspaceContainer } from "./smartfocus-components";
import WorkspaceBGColor from "../../../workspace/workspace-bgcolor";
import WorkspaceBG from "../../../workspace/workspace-bg";
import WorkspaceItem from "../../../workspace/workspace-item";
import { updateTimelineTime } from "../../../../redux/actions/timelineUtils";
import { ANIMO_RENDER, STATIC_PATH } from "../../../../constants/config";
import { getMaskContainerHeight } from "../../../../helper/smartfocusHelper";
import { isVideoOnly, isImageOnly } from "../../../timeline/timeline-helper";


const SmartfocusWorkspace = ({ isOpen, scaleValue: propScaleValue, initialPosition, isLock }) => {
  const scaleValue = Math.min(propScaleValue, 100);
  const containerRef = useRef(null);
  const isDragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const dispatch = useDispatch();
  const [updatedZoomData, setUpdatedZoomData] = useState(null);
  const zoomPreviewRef = useRef(null);
  const zoomFactor = useSelector((state) => state.app.get("zoomFactor"));

  const appReducer = useSelector((state) => state.app);
  const projectDetails = useSelector((state) => state.projectDetails);
  const selectedWorkspaceItem = appReducer.get("selectedItems");
  const projectWidth = projectDetails.get("width");
  const projectHeight = projectDetails.get("height");
  const playhead = appReducer.get("playhead");
  const isPlayAll = appReducer.get("isPlayAll");
  const libraryDragItem = appReducer.get("libraryDragItem");
  const browserName = appReducer.get("browserName");
  const selectedItems = appReducer.get("selectedItems");
  const swapDetails = appReducer.get("swapDetails");
  const prefetchToken = appReducer.get("prefetchToken");
  const bgColor = projectDetails.get("bgColor");
  const selectedValue = appReducer.get("selectedSubtitles");
  const selectedItem = selectedValue.size > 0 ? selectedValue?.get(0)?.split("-")[0] : selectedWorkspaceItem.get(0);
  const item = projectDetails.getIn(["workspaceItems", selectedItem]);
  const { dimensionName } = useSelector((state) => state.userDetails);

  // Determine if the workspace is vertical based on project dimensions
  const isVerticalWorkspace = projectHeight > projectWidth && projectWidth > 0 && projectHeight > 0;
  const isSquareWorkspace = projectHeight === projectWidth && projectWidth > 0 && projectHeight > 0;
  const maskHeight = getMaskContainerHeight(projectWidth , projectHeight) 
  // Set dimensions for vertical and horizontal workspaces
  const VERTICAL_CONTAINER_WIDTH = 110;
  const VERTICAL_CONTAINER_HEIGHT = 195;
  const HORIZONTAL_CONTAINER_WIDTH = 292;
  const HORIZONTAL_CONTAINER_HEIGHT = maskHeight + 0.5
  const SQUARE_CONTAINER_WIDTH = 164;
  const SQUARE_CONTAINER_HEIGHT = 164;


  // Dynamically set container dimensions based on workspace orientation
  const CONTAINER_WIDTH = isVerticalWorkspace ? VERTICAL_CONTAINER_WIDTH : (isSquareWorkspace ? SQUARE_CONTAINER_WIDTH : HORIZONTAL_CONTAINER_WIDTH);
  const CONTAINER_HEIGHT = isVerticalWorkspace ? VERTICAL_CONTAINER_HEIGHT :
    (isSquareWorkspace ? SQUARE_CONTAINER_HEIGHT : HORIZONTAL_CONTAINER_HEIGHT);

  // Dynamically set mask dimensions based on workspace orientation
  const BASE_MASK_SIZE_WIDTH = isVerticalWorkspace ? VERTICAL_CONTAINER_WIDTH :
    (isSquareWorkspace ? SQUARE_CONTAINER_WIDTH : HORIZONTAL_CONTAINER_WIDTH);
  const BASE_MASK_SIZE_HEIGHT = isVerticalWorkspace ? VERTICAL_CONTAINER_HEIGHT : 
    (isSquareWorkspace ? SQUARE_CONTAINER_HEIGHT : maskHeight);

  // Extract initial width and height from the item's zoomData
  const initialZoomData = item?.get("zoomData")?.toJS();
  const pWidth = projectWidth;
  const pHeight = projectHeight;

  const firstZoomStage = initialZoomData ? Object.values(initialZoomData)[0]?.width : null;
  const INITIAL_WIDTH = firstZoomStage?.width || pWidth;
  const INITIAL_HEIGHT = firstZoomStage?.height || pHeight;

  // Calculate mask size based on scaleValue percentage
  const MASK_SIZE_WIDTH = (BASE_MASK_SIZE_WIDTH * scaleValue) / 100;
  const MASK_SIZE_HEIGHT = (BASE_MASK_SIZE_HEIGHT * scaleValue) / 100;

  // Calculate dynamic workspace dimensions based on scaleValue
  const WORKSPACE_WIDTH = (INITIAL_WIDTH * scaleValue) / 100;
  const WORKSPACE_HEIGHT = (INITIAL_HEIGHT * scaleValue) / 100;

  // Calculate initial position based on the given workspace dimensions
  const calculateInitialPosition = (initialPosition) => {
    const scaledX = (initialPosition.x / INITIAL_WIDTH) * CONTAINER_WIDTH;
    const scaledY = (initialPosition.y / INITIAL_HEIGHT) * CONTAINER_HEIGHT;
    return { x: scaledX, y: scaledY };
  };

  const [position, setPosition] = useState(() =>
    calculateInitialPosition(initialPosition || { x: 0, y: 0 })
  );

  if (!isOpen) {
    return null;
  }

  const previewZoomEffect = (itemId, zoomData, item) => {
    const itemEl = document.getElementById(itemId);
    if (!itemEl || !zoomData || !item?.size) return;
    const enterStart = item.get("enterStart") || 0;
    const videoStart = item.get("videoStart") || 0;
    const itemEnter = enterStart - videoStart;
    // Kill any existing tweens on this element

    const overlayDiv = document.getElementById(`${item.get("id")}`);
    TweenMax.killTweensOf(itemEl);
    const itemWidth = projectDetails.get("width")
    const itemHeight = projectDetails.get("height")

    Object.entries(zoomData).forEach(([, data]) => {
      if (!data) return; // Ensure data?.zoom is valid before proceeding
      const zoomIn = Object.values(data).find(stage => stage.type === 'zoomIn');
      const zoomOut = Object.values(data).find(stage => stage.type === 'zoomOut');

      // If we have both zoomIn and zoomOut, check if playhead is within the zoom range
      if (zoomIn && zoomOut) {
        const zoomRangeStart = zoomIn.start + itemEnter;
        const zoomRangeEnd = zoomOut.end + itemEnter;
        if (playhead >= zoomRangeStart && playhead <= zoomRangeEnd) {

          Object.entries(data).forEach(([, zoom]) => {

            if (!zoom.disableZoomOut && zoom.type === "zoomIn") {
              // Zoom region inside the video
              const zoomW = zoom.width;
              const zoomH = zoom.height;

              const dx = zoom.x - item.get("x")
              const dy = (zoom.y - item.get("y"))
              const zScaleX = (itemWidth * zoomFactor) / (zoomW * zoomFactor);
              const zScaleY = (itemHeight * zoomFactor) / (zoomH * zoomFactor);
              const correctedWidth = (dx * ((itemWidth * zoomFactor) / itemWidth));
              const correctedHeight = (dy * ((itemHeight * zoomFactor) / itemHeight));
              const zX = zScaleX * correctedWidth * -1;
              const zY = zScaleY * correctedHeight * -1;
              const tweenX = zX
              const tweenY = zY
              // Apply the zoom effect
              TweenMax.to(overlayDiv, 0.4, {
                x: tweenX,
                y: tweenY,
                scaleX: zScaleX,
                scaleY: zScaleY,
                transformOrigin: "0 0",
                ease: "power1.inOut",
              });
            }
          });
        }
      }
    });
  };

  const resetZoomEffect = (itemId) => {
    const itemEl = document.getElementById(itemId);
    const xVal = item.get("x");
    const yVal = item.get("y");
    if (!itemEl) return;
    TweenMax.killTweensOf(itemEl);

    TweenMax.to(itemEl, {
      x: xVal * zoomFactor,
      y: yVal * zoomFactor,
      scaleX: 1,
      scaleY: 1,
      transformOrigin: '0 0',
      ease: 'power1.inOut'
    });
  };

  useEffect(() => {
    if (!selectedItem) {
      return () => { }; // 👈 empty cleanup keeps ESLint happy
    }

    const item = projectDetails.getIn(["workspaceItems", selectedItem]);
    const zoomData = item?.get("zoomData")?.toJS();

    if (zoomData && Object.keys(zoomData).length > 0) {
      previewZoomEffect(selectedItem, zoomData, item);
      zoomPreviewRef.current = { itemId: selectedItem };
    } else if (zoomPreviewRef.current?.itemId === selectedItem) {
      resetZoomEffect(selectedItem, undefined, item);
      zoomPreviewRef.current = null;
    }

    // Cleanup function
    return () => {
      if (zoomPreviewRef.current) {
        resetZoomEffect(zoomPreviewRef.current.itemId, undefined, item);
        zoomPreviewRef.current = null;
      }
    };
  }, [selectedItem, projectDetails, playhead]);

  // Add this effect near your other useEffect hooks
  useEffect(() => {
    // This cleanup function will run when the component unmounts
    return () => {
      // Reset any active zoom effects
      if (zoomPreviewRef.current) {
        const { itemId } = zoomPreviewRef.current;
        const item = projectDetails.getIn(["workspaceItems", itemId]);
        if (item) {
          resetZoomEffect(itemId, undefined, item);
        }
        zoomPreviewRef.current = null;
      }

      // Clean up any TweenMax instances
      if (containerRef.current) {
        TweenMax.killTweensOf(containerRef.current);
      }

      // Clean up any workspace item tweens
      if (selectedItem) {
        const itemEl = document.getElementById(selectedItem);
        if (itemEl) {
          const xVal = item.get("x");
          const yVal = item.get("y");
          TweenMax.killTweensOf(itemEl);
          // Reset the element's transform
          TweenMax.set(itemEl, {
            x: xVal * zoomFactor,
            y: yVal * zoomFactor,
            scaleX: 1,
            scaleY: 1,
            transformOrigin: '0 0'
          });
        }
      }
    };
  }, []); // Empty dependency array means this runs on mount/unmount only


  useEffect(() => {
    if (!item) return;
    const enterStart = item.get("enterStart") || 0;
    const videoStart = item.get("videoStart") || 0;
    const itemEnter = enterStart - videoStart;
    const zoomData = item.get("zoomData");
    if (!zoomData) return;

    const zoomGroups = zoomData.toJS();
    let found = false;

    // Check each zoom group to see if playhead is within the zoom range (zoomIn start to zoomOut end)
    Object.entries(zoomGroups).some(([, zoomGroup]) => {
      // Find zoomIn and zoomOut in this group
      const zoomIn = Object.values(zoomGroup).find(stage => stage.type === 'zoomIn');
      const zoomOut = Object.values(zoomGroup).find(stage => stage.type === 'zoomOut');

      // If we have both zoomIn and zoomOut, check if playhead is within the zoom range
      if (zoomIn && zoomOut) {
        const zoomRangeStart = zoomIn.start + itemEnter;
        const zoomRangeEnd = zoomOut.end + itemEnter;

        if (playhead >= zoomRangeStart && playhead <= zoomRangeEnd) {
          // Use zoomIn's x, y values for the entire zoom range
          const { x, y } = zoomIn;
          const newPosition = calculateInitialPosition({ x, y });
          setPosition(newPosition);
          found = true;
          return true; // Exit the iteration
        }
      }
      return false;
    });

    // If no matching zoom range found, you could add logic here to handle that case
    // For example, reset to default position or show add zoom button
    if (!found) {
      // setAddZoom(true);
    }
  }, [playhead, item]);

  useEffect(() => {
    // Update dimensions dynamically when scaleValue changes
    const updateDimensionsOnScaleChange = () => {
      const enterStart = item.get("enterStart") || 0;
      const videoStart = item.get("videoStart") || 0;
      const itemEnter = enterStart - videoStart;
      const newWidth = (INITIAL_WIDTH * scaleValue) / 100;
      const newHeight = (INITIAL_HEIGHT * scaleValue) / 100;

      // Adjust x and y to ensure the mask stays within boundaries
      setPosition((prevPosition) => ({
        x: Math.max(0, Math.min(CONTAINER_WIDTH - MASK_SIZE_WIDTH, prevPosition.x)),
        y: Math.max(0, Math.min(CONTAINER_HEIGHT - MASK_SIZE_HEIGHT, prevPosition.y)),
      }));

      // Update zoomData dimensions
      if (item) {
        const zoomData = item.get("zoomData");
        if (zoomData) {
          const updatedZoomData = zoomData.toJS();
          const zoomGroups = Object.values(updatedZoomData);

          zoomGroups.forEach((zoomGroup) => {
            const zoomIn = Object.values(zoomGroup).find(stage => stage.type === 'zoomIn');
            const zoomOut = Object.values(zoomGroup).find(stage => stage.type === 'zoomOut');
            if (zoomIn && zoomOut) {
              const zoomRangeStart = zoomIn.start + itemEnter;
              const zoomRangeEnd = zoomOut.end + itemEnter;
              if (playhead >= zoomRangeStart && playhead <= zoomRangeEnd) {
                // Adjust zoomIn dimensions and position
                zoomIn.width = newWidth;
                zoomIn.height = newHeight;
                zoomIn.x = Math.min(zoomIn.x, projectWidth - newWidth);
                zoomIn.y = Math.min(zoomIn.y, projectHeight - newHeight);
              }
            }
          });
          setUpdatedZoomData(updatedZoomData);
          const toUpdate = [
            {
              id: item.get("id"),
              container: "workspaceItems",
              toUpdate: {
                zoomData: updatedZoomData,
              },
            },
          ];

          if (toUpdate.length) {
            dispatch(updateTimelineTime({ toUpdate }));
          }
        }
      }
    };

    if (item && item.size > 0) {
      updateDimensionsOnScaleChange();
    }

  }, [scaleValue]);

  useEffect(() => {
    if (isLock) {
      isDragging.current = false;
    }
  }, [isLock]);

  const handleMouseDown = (e) => {
    if (isLock) return;
    isDragging.current = true;
    const rect = containerRef.current.getBoundingClientRect();
    offset.current = {
      x: e.clientX - rect.left - position.x,
      y: e.clientY - rect.top - position.y,
    };
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current || isLock) {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    let newX = e.clientX - rect.left - offset.current.x;
    let newY = e.clientY - rect.top - offset.current.y;

    // Constrain inside container
    newX = Math.max(0, Math.min(CONTAINER_WIDTH - MASK_SIZE_WIDTH, newX));
    newY = Math.max(0, Math.min(CONTAINER_HEIGHT - MASK_SIZE_HEIGHT, newY));
    const MIN_Y = 0;
    const MAX_Y = isVerticalWorkspace || isSquareWorkspace ? 177 : maskHeight - MASK_SIZE_HEIGHT;

    // Clamp Y between MIN_Y and MAX_Y
    newY = Math.max(MIN_Y, Math.min(MAX_Y, newY));
    setPosition({ x: newX, y: newY });

    // Update zoomData with new x, y, width, and height values
    if (item) {
      const enterStart = item.get("enterStart") || 0;
      const videoStart = item.get("videoStart") || 0;
      const itemEnter = enterStart - videoStart;

      const zoomData = item.get("zoomData");
      if (zoomData) {
        const updatedZoomData = zoomData.toJS();
        const zoomGroups = Object.values(updatedZoomData);

        zoomGroups.forEach((zoomGroup) => {
          const zoomIn = Object.values(zoomGroup).find(stage => stage.type === 'zoomIn');
          const zoomOut = Object.values(zoomGroup).find(stage => stage.type === 'zoomOut');

          if (zoomIn && zoomOut) {
            const zoomRangeStart = zoomIn.start + itemEnter;
            const zoomRangeEnd = zoomOut.end + itemEnter;

            if (playhead >= zoomRangeStart && playhead <= zoomRangeEnd) {
              const zoomY = Math.min((newY / CONTAINER_HEIGHT) * INITIAL_HEIGHT, projectHeight - WORKSPACE_HEIGHT);
              const zoomX = Math.min((newX / CONTAINER_WIDTH) * INITIAL_WIDTH, projectWidth - WORKSPACE_WIDTH);
              zoomIn.x = zoomX
              zoomIn.y = zoomY;
              zoomIn.width = WORKSPACE_WIDTH;
              zoomIn.height = WORKSPACE_HEIGHT;

              zoomOut.x = 0;
              zoomOut.y = 0;
              zoomOut.width = pWidth;
              zoomOut.height = pHeight;
            }
          }
        });
        setUpdatedZoomData(updatedZoomData);
      }
    }
  };

  const handleMouseUp = () => {
    if (isLock || !isDragging.current) return;

    if (updatedZoomData) {
      const toUpdate = [
        {
          id: item.get("id"),
          container: "workspaceItems",
          toUpdate: {
            zoomData: updatedZoomData,
          },
        },
      ];

      if (toUpdate.length) {
        dispatch(updateTimelineTime({ toUpdate }));
      }
    }

    isDragging.current = false;
  };

  const handleTouchStart = (e) => {
    if (isLock) return;

    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();

    // Calculate the offset from the touch point to the top-left corner of the mask
    offset.current = {
      x: touch.clientX - rect.left - position.x,
      y: touch.clientY - rect.top - position.y
    };

    isDragging.current = true;
    e.preventDefault(); // Prevent default touch behavior
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current || isLock) return;

    const touch = e.touches[0];
    const rect = containerRef.current.getBoundingClientRect();

    let newX = touch.clientX - rect.left - offset.current.x;
    let newY = touch.clientY - rect.top - offset.current.y;

    // Apply constraints
    const MIN_Y = 0;
    const MAX_Y = CONTAINER_HEIGHT - MASK_SIZE_HEIGHT;

    // Constrain inside container
    newX = Math.max(0, Math.min(CONTAINER_WIDTH - MASK_SIZE_WIDTH, newX));
    newY = Math.max(MIN_Y, Math.min(MAX_Y, newY));

    setPosition({ x: newX, y: newY });

    // Update zoom data if needed
    if (item) {
      const enterStart = item.get("enterStart") || 0;
      const videoStart = item.get("videoStart") || 0;
      const itemEnter = enterStart - videoStart;

      const zoomData = item.get("zoomData");
      if (zoomData) {
        const updatedZoomData = zoomData.toJS();
        const zoomGroups = Object.values(updatedZoomData);

        zoomGroups.forEach((zoomGroup) => {
          // Find zoomIn and zoomOut in this group
          const zoomIn = Object.values(zoomGroup).find(stage => stage.type === 'zoomIn');
          const zoomOut = Object.values(zoomGroup).find(stage => stage.type === 'zoomOut');

          // If we have both zoomIn and zoomOut, check if playhead is within the zoom range
          if (zoomIn && zoomOut) {
            const zoomRangeStart = zoomIn.start + itemEnter;
            const zoomRangeEnd = zoomOut.end + itemEnter;

            if (playhead >= zoomRangeStart && playhead <= zoomRangeEnd) {
              // Only update zoomIn's position, keep zoomOut at default
              zoomIn.x = (newX / CONTAINER_WIDTH) * INITIAL_WIDTH;
              zoomIn.y = (newY / CONTAINER_HEIGHT) * INITIAL_HEIGHT;
              zoomIn.width = WORKSPACE_WIDTH;
              zoomIn.height = WORKSPACE_HEIGHT;

              // Reset zoomOut to default values
              zoomOut.x = 0;
              zoomOut.y = 0;
              zoomOut.width = INITIAL_WIDTH;
              zoomOut.height = INITIAL_HEIGHT;
            }
          }
        });
        setUpdatedZoomData(updatedZoomData);
      }
    }

    e.preventDefault(); // Prevent default touch behavior
  };

  const handleTouchEnd = (e) => {
    if (isLock || !isDragging.current) return;

    isDragging.current = false;

    if (updatedZoomData) {
      const toUpdate = [
        {
          id: item.get("id"),
          container: "workspaceItems",
          toUpdate: {
            zoomData: updatedZoomData,
          },
        },
      ];

      if (toUpdate.length) {
        dispatch(updateTimelineTime({ toUpdate }));
      }
    }
    isDragging.current = false;
    e.preventDefault(); // Prevent default touch behavior
  };

  useEffect(() => {
    const handleDocumentMouseUp = () => {
      if (isDragging.current) {
        handleMouseUp();
      }
    };

    const handleDocumentTouchEnd = () => {
      if (isDragging.current) {
        handleTouchEnd();
      }
    };

    document.addEventListener('mouseup', handleDocumentMouseUp);
    document.addEventListener('touchend', handleDocumentTouchEnd);

    return () => {
      document.removeEventListener('mouseup', handleDocumentMouseUp);
      document.removeEventListener('touchend', handleDocumentTouchEnd);
    };
  }, [updatedZoomData, item]);

  const getWorkspaceBGList = (previewZoomFactor) => {
    const bgItems = [];
    projectDetails.get("workspaceBG").forEach((bgItem) => {
      const isVisible =
        playhead >= bgItem.get("playStart") &&
        playhead <= bgItem.get("playEnd");
      if (isPlayAll || isVisible) {
        bgItems.push(
          <WorkspaceBG
            key={bgItem.get("id")}
            isVisible={isVisible}
            projectWidth={projectWidth}
            projectHeight={projectHeight}
            workspaceBG={bgItem}
            zoomFactor={previewZoomFactor}
            browserName={browserName}
            playhead={playhead}
          />
        );
      }
    });
    return bgItems;
  };

  const getHoverClass = (item) => {
    let result = "";
    const isSelected = selectedItems.includes(item.get("id"));

    if (isSelected) {
      return result;
    }

    if (!result) {
      result = "ws-hover-default";
    }
    return result;
  };

  const getWorkspaceItemList = (previewZoomFactor) => {
    const items = [];
    projectDetails.get("workspaceItems").forEach((item) => {
      if (isVideoOnly(item.get("type"), item.get("subtype")) || isImageOnly(item.get("type"), item.get("subtype"))) {
        let isVisible =
          playhead >= item.get("enterStart") &&
          playhead <= item.get("exitEnd");
        isVisible = ANIMO_RENDER || isVisible;
        const isSelected = selectedItems.includes(item.get("id"));
        const renderAnyway = item.get("type") === "TEXT";

        if (isPlayAll || isSelected || isVisible || renderAnyway) {
          const groupChildren = null;
          const isClickable = isVisible;
          let pointerEvents = "none"; // Disable pointer events in preview

          if (!isClickable) {
            pointerEvents = "none";
          }

          items.push(
            <WorkspaceItem
              key={item.get("id")}
              className={getHoverClass(item)}
              browserName={browserName}
              id={item.get("id")}
              isVisible={isVisible}
              item={item}
              opacity={1}
              pointerEvents={pointerEvents}
              projectWidth={projectWidth}
              projectHeight={projectHeight}
              zoomFactor={previewZoomFactor}
              selectedItems={selectedItems}
              isPlayAll={isPlayAll}
              setPlayAll={() => { }}
              addTween={() => { }}
              swapDetails={swapDetails}
              prefetchToken={prefetchToken}
              prefetchComplete={() => { }}
              isMouseOnSelectionBox={() => false}
              libraryDragItem={libraryDragItem}
              onPointerUp={() => { }}
            >
              {groupChildren}
            </WorkspaceItem>
          );
        }
      }
    })

    return items;
  };

  // Calculate zoomFactor for smart focus preview
  // When main workspace has zoomFactor=1 (800x450), 
  // smart focus container should show full workspace in 292x170
  // Use BASE_MASK_SIZE_WIDTH for workspace calculation (static)
  const smartFocusZoomFactor = BASE_MASK_SIZE_WIDTH / projectWidth; // 292 / 800 = 0.365

  const bgItems = getWorkspaceBGList(smartFocusZoomFactor);
  const workspaceItems = getWorkspaceItemList(smartFocusZoomFactor);

  // Calculate workspace dimensions using smart focus zoomFactor (static)
  const scaledWorkspaceWidth = projectWidth * smartFocusZoomFactor;
  const scaledWorkspaceHeight = projectHeight * smartFocusZoomFactor;

  // Adjust scene styles to center the workspace
  const sceneStyles = {
    width: `${isVerticalWorkspace ? VERTICAL_CONTAINER_WIDTH : isSquareWorkspace ? SQUARE_CONTAINER_WIDTH : scaledWorkspaceWidth}px`,
    height: `${isVerticalWorkspace ? VERTICAL_CONTAINER_HEIGHT : isSquareWorkspace ? SQUARE_CONTAINER_HEIGHT : scaledWorkspaceHeight}px`,
    overflowX: "hidden",
    overflowY: "hidden",
    top: "0px",
    left: isVerticalWorkspace || isSquareWorkspace ? "0px" : "auto",
    margin: isVerticalWorkspace || isSquareWorkspace ? "0 auto" : "0",
  };

  const workspace = (
    <WorkspaceContainer style={sceneStyles}>
      <WorkspaceBGColor bgColor={bgColor} />
      {bgItems}
      {workspaceItems}
      {/* {subtitleItems} */}
    </WorkspaceContainer>
  );

  // Adjust overlay elements for vertical workspace
  const overlayElements = (
    <OverlayContainer>
      <Overlay
        className="top-overlay"
        top={0}
        left={0}
        width={CONTAINER_WIDTH}
        height={position.y}
        tr={0}
        tl={0}
      />
      <Overlay
        className="left-overlay"
        top={position.y + MASK_SIZE_HEIGHT}
        left={0}
        width={CONTAINER_WIDTH}
        height={CONTAINER_HEIGHT - (position.y + MASK_SIZE_HEIGHT)}
        br={0}
        bl={0}
      />
      <Overlay
        className="right-overlay"
        top={position.y}
        left={0}
        width={position.x}
        height={MASK_SIZE_HEIGHT}
      />
      <Overlay
        className="bottom-overlay"
        top={position.y}
        left={position.x + MASK_SIZE_WIDTH}
        width={CONTAINER_WIDTH - (position.x + MASK_SIZE_WIDTH)}
        height={MASK_SIZE_HEIGHT}
      />
      <MaskArea
        className="mask-area"
        onMouseDown={handleMouseDown}
        onTouchStart={handleMouseDown}
        style={{
          left: position.x,
          top: position.y,
          width: `${MASK_SIZE_WIDTH}px`,
          height: `${MASK_SIZE_HEIGHT}px`,
        }}
      >
        <CenterCircle>
          {isLock ? (
            <img src={`${STATIC_PATH}${"zoom-lock.svg"}`} alt="" />
          ) : (
            <img src={`${STATIC_PATH}${"zoom-move-icon.svg"}`} alt="" />
          )}
        </CenterCircle>
      </MaskArea>
    </OverlayContainer>
  );

  useEffect(() => {
    if (!item) return;

    const zoomData = item.get("zoomData")?.toJS();
    if (zoomData) {
      setUpdatedZoomData(zoomData);
    }
  }, [item]);

  return (
    <SfWorkspaceContainer
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchMove={handleTouchMove}  // Add touch move handler
      onTouchEnd={handleTouchEnd}    // Keep touch end handler
      onTouchStart={handleTouchStart} // Add touch start handler
      style={{
        width: `${CONTAINER_WIDTH}px`,
        height: `${CONTAINER_HEIGHT}px`,
        position: 'relative',
        touchAction: 'none',  // Prevent default touch behaviors
        top: dimensionName === "vertical" ? "0px" : "13px"
      }}
    >
      <SmartWorkspaceWrapper
        className="smartfocus-workspace-wrapper"
        $isPlayAll={isPlayAll}
        isAnimoPlayer={false}
      >
        {workspace}
        {overlayElements}
      </SmartWorkspaceWrapper>
    </SfWorkspaceContainer>
  );
};


SmartfocusWorkspace.propTypes = {
  isOpen: PropTypes.string,
  scaleValue: PropTypes.number,
  isLock: PropTypes.bool,
  initialPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number,
  })
};



export default SmartfocusWorkspace;
